import { useEffect, useRef, useState } from 'react';
import { If } from '../Atoms/Conditionals';
import { Tooltip } from '../Atoms/Tooltip';
import { useWindowDimensions } from '../hooks/use-window-dimension';
import { Heading, HeadingType } from '../Typography';
import { usePaytaxCalcContext } from './context';
import { TaxRegimeRecommendation } from './recommendation-strip';
import TabComponent from './tab-switch-section';
import { tabsDataIncomeTax } from './tax-calculator-constants';
import TaxLiabilityCard from './tax-liability-new-card';
import { TaxSummary } from './tax-summary';
import { CALCULATOR_DATA, TAX_RESULT, taxRegimeMap } from './types';
import { useDebouncedTaxCalculation } from './use-debounce-calcultion';
import { urlParamToInputFieldMap } from './constants';
import { toggleFinancialYear } from './utils';

const getCurrentFinancialYear = () => {
  const currentYear = '2026';
  return currentYear;
};

export const IncomeTaxCalculator = ({
  showSummary,
  showSlabsTable = false,
}: {
  showSummary: boolean;
  showSlabsTable?: boolean;
}) => {
  const { financialYearCalc, financialYear, setFinancialYear } =
    usePaytaxCalcContext();
  const [showCalculation, setShowCalculation] = useState({
    displayCalculation: false,
    renderCalculation: false,
  });
  const { width } = useWindowDimensions();
  const isDesktop = !!width && width >= 768;
  const taxWrapper = useRef<HTMLDivElement>(null);
  const calculationFieldsWrapper = useRef<HTMLDivElement>(null);
  const [hasError, setGlobalError] = useState(false);
  const [inputTabData, setInputData] = useState<CALCULATOR_DATA>({
    Year: getCurrentFinancialYear(),
    AgeGroup: 'NotSenior',
  });
  const [taxResult, setTaxResult] = useState<TAX_RESULT | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const commonProps = {
    hasError: hasError,
    setGlobalError: setGlobalError,
    inputTabData: inputTabData,
    setInputData: setInputData,
    showCalculation: showCalculation,
    setShowCalculation: setShowCalculation,
    taxWrapper: taxWrapper.current,
    setTaxResult: setTaxResult,
  };

  const handleCalculateFor = () => {
    setShowCalculation((prev) => ({
      ...prev,
      displayCalculation: false,
    }));
    const year = financialYearCalc === '2026' ? '2025' : '2026';
    setFinancialYear(year);
  };

  const handleRecalculate = () => {
    setShowCalculation((prev) => ({
      ...prev,
      displayCalculation: false,
    }));
    setInputData((prev) => ({ ...prev, Year: financialYearCalc }));
    setFinancialYear(financialYearCalc);
  };

  const { setCurrentState } = useDebouncedTaxCalculation(
    inputTabData,
    setTaxResult,
    0
  );

  const handleFieldsFromURL = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const updatedData: Partial<CALCULATOR_DATA> = {};
    let anyFieldUpdated = false;
    for (const [param, field] of Object.entries(urlParamToInputFieldMap)) {
      const val = searchParams.get(param);
      if (val !== null) {
        const numericVal = Number(val.replace(/,/g, ''));
        if (!isNaN(numericVal)) {
          updatedData[field] = numericVal;
          anyFieldUpdated = true;
        }
      }
    }

    const shouldShowCalc = searchParams.get('viewCalculation') === 'true';
    if (anyFieldUpdated || shouldShowCalc) {
      setActiveTab(1);
      setInputData((prev) => ({
        ...prev,
        ...updatedData,
        Year: toggleFinancialYear(financialYearCalc),
      }));

      if (shouldShowCalc) {
        setShowCalculation({
          displayCalculation: true,
          renderCalculation: true,
        });
      }

      const salary = searchParams.get('incomeSalary');
      if (salary) {
        setCurrentState(salary);
      }
    }
  };

  useEffect(() => {
    handleFieldsFromURL();
  }, []);

  return (
    <div className="flex gap-10 sm:flex-col">
      <div
        className={`w-full
        } sm:w-full`}
      >
        <div
          className={`p-12 md:p-2.5 shadow-lg bg-white mb-6 rounded-md sm:p-4 w-auto ${
            showCalculation.displayCalculation ? 'block' : 'h-0 hidden'
          }`}
          style={{
            boxShadow: '0px 0px 30px 0px #1678FB1A',
          }}
          id="tax-calculation"
          ref={taxWrapper}
        >
          <If condition={showCalculation.displayCalculation}>
            <div className="flex justify-between items-baseline cursor-pointer mb-6 text-s-18 font-bold sm:flex-wrap">
              <h3 className="flex gap-3 items-center text-s-24 md:text-base font-bold text-font-500 sm:pb-3">
                Summary -
                {financialYearCalc === '2026'
                  ? ' FY 2026-2027 (AY 2027-2028)'
                  : ' FY 2025-2026 (AY 2026-2027)'}
                <span>
                  <Tooltip
                    content={
                      <div className="bg-[#041832] px-2 py-2 z-20 relative mt-1 rounded-sm flex justify-center text-white text-center w-7/12 md:w-11/12 sm:w-full leading-6 text-base">
                        The financial year (FY) is the period during which
                        income is earned (e.g., April 1 to March 31), while the
                        assessment year (AY) is the year after that, when the
                        income is assessed and taxed.
                      </div>
                    }
                    hideCloseIcon={true}
                    showOnHover={true}
                    icon=""
                    direction="bottom"
                    contentPosition={'absolute'}
                  >
                    <img
                      src="https://assets.clear.in/cleartax/images/1706006936_info.svg"
                      alt=""
                      width={24}
                      height={24}
                    />
                  </Tooltip>
                </span>
              </h3>
              <div className="flex gap-6 md:gap-2 sm:gap-2">
                <div
                  className="text-blue-350 text-s-16 font-medium flex gap-2 items-center text-base sm:text-s-12"
                  onClick={handleCalculateFor}
                >
                  <img
                    className="w-4 h-4 sm:h-3 sm:w-3"
                    src="https://assets.clear.in/cleartax/images/1738306370_group1814219280.png"
                    alt="calculate for"
                  />{' '}
                  Calculate for{' '}
                  {financialYearCalc === '2026'
                    ? 'FY 2025-2026'
                    : 'FY 2026-2027'}
                </div>
                <div
                  className="text-blue-350 text-s-16 font-medium flex gap-2 items-center text-base sm:text-s-12"
                  onClick={handleRecalculate}
                >
                  <img
                    className="w-4 h-4 sm:h-3 sm:w-3"
                    src="https://assets.clear.in/cleartax/images/1738306187_arrowcounterclockwise.png"
                    alt="calculate for"
                  />
                  Recalculate
                </div>
              </div>
            </div>
            {taxResult && (
              <TaxRegimeRecommendation
                taxDifference={
                  taxResult && taxRegimeMap[0] && taxRegimeMap[1]
                    ? Math.abs(
                        Number(taxResult[taxRegimeMap[0]]?.grossTax || 0) -
                          Number(taxResult[taxRegimeMap[1]]?.grossTax || 0)
                      )
                    : 0
                }
                suggestedTaxScheme={
                  taxResult &&
                  taxRegimeMap[0] &&
                  taxRegimeMap[1] &&
                  Number(taxResult[taxRegimeMap[1]]?.grossTax || 0) <
                    Number(taxResult[taxRegimeMap[0]]?.grossTax || 0)
                    ? 'Old'
                    : 'New'
                }
              />
            )}
            <TaxSummary
              inputTabData={inputTabData}
              renderCalculation={showCalculation.renderCalculation}
            />
          </If>
        </div>
        <div
          className="p-12 sm:p-6 bg-white my-6"
          style={{
            boxShadow: '0px 0px 30px 0px #1678FB1A',
          }}
          ref={calculationFieldsWrapper}
        >
          <If condition={showCalculation.displayCalculation}>
            <Heading
              variant={HeadingType.h6}
              content="Recalculate"
              className="mb-6 sm:mb-2 sm:ml-2"
            />
          </If>
          <TabComponent
            tabsData={tabsDataIncomeTax}
            showSlabsTable={showSlabsTable}
            defaultActiveTab={activeTab}
            commonProps={commonProps}
            selectedFinancialYear={financialYear}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        </div>
      </div>
      <If
        condition={
          showSummary && ((activeTab === 0 && !isDesktop) || activeTab !== 0)
        }
      >
        <div className="w-4/12 lg:flex-shrink-0 xl:flex-shrink-0 mt-6  sm:w-full sm:fixed sm:bottom-0 sm:left-0  sm:z-100 sm:mb-7">
          <TaxLiabilityCard taxData={taxResult} />
        </div>
      </If>
    </div>
  );
};
